import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {
  AlertDialogComponent,
  DialogOption as AlertDialogOption,
} from '@components/shared/alert-dialog/alert-dialog.component';
import { firstValueFrom } from 'rxjs';

/** ダイアログのViewModel */
export type DeviceTypeEditorDialogViewModel = {
  /** 更新するか追加するかのフラグ */
  updating: boolean;
  target?: EditedDeviceTypeViewModel;
};

/** デバイス種類のViewModel */
export type EditedDeviceTypeViewModel = {
  /** システム内部のデバイス種類キー */
  deviceTypeKey: string;
  /** デバイス種類ID */
  deviceTypeId: string;
  /** 表示名 */
  displayName: string;
  /** 説明 */
  description?: string;
  /** 測定間隔選択肢(CSV形式)*/
  samplingFrequency?: number[];
  /** 送信間隔選択肢(CSV形式) */
  reportingFrequency?: number[];
  /** 温度センサー */
  temperatureSensor?: boolean;
  /** 気圧センサー */
  pressureSensor?: boolean;
  /** 湿度センサー */
  humiditySensor?: boolean;
  /** 傾斜センサー */
  tiltSensor?: boolean;
  /** 照度センサー */
  lightSensor?: boolean;
  /** 位置即位 */
  locationSensor?: boolean;
  /** 位置即位技術(GNSS) */
  locationTechGnss?: boolean;
  /** 位置即位技術(Wi-Fi) */
  locationTechWifi?: boolean;
  /** 位置即位技術(Cellular) */
  locationTechCellular?: boolean;
  /** 落下検知 */
  fallDetected?: boolean;
  /** 動作検知 */
  mcdDetected?: boolean;
  /** 明暗検知 */
  lightExposureDetected?: boolean;
  /** 衝撃検知 */
  shockDetected?: boolean;
  /** フライトモード */
  fmd?: boolean;
};

/** フォームグループ */
type DeviceTypeEditorDialogFormGroup = FormGroup<{
  /** デバイス種類ID */
  deviceTypeId: FormControl<string>;
  /** 表示名 */
  displayName: FormControl<string>;
  /** 説明 */
  description: FormControl<string>;
  /** 測定間隔選択肢(CSV形式)*/
  samplingFrequency: FormControl<string>;
  /** 送信間隔選択肢(CSV形式) */
  reportingFrequency: FormControl<string>;
  /** 温度センサー */
  temperatureSensor: FormControl<boolean>;
  /** 気圧センサー */
  pressureSensor: FormControl<boolean>;
  /** 湿度センサー */
  humiditySensor: FormControl<boolean>;
  /** 傾斜センサー */
  tiltSensor: FormControl<boolean>;
  /** 照度センサー */
  lightSensor: FormControl<boolean>;
  /** 位置即位 */
  locationSensor: FormControl<boolean>;
  /** 位置即位技術(GNSS) */
  locationTechGnss: FormControl<boolean>;
  /** 位置即位技術(Wi-Fi) */
  locationTechWifi: FormControl<boolean>;
  /** 位置即位技術(Cellular) */
  locationTechCellular: FormControl<boolean>;
  /** 落下検知 */
  fallDetected: FormControl<boolean>;
  /** 動作検知 */
  mcdDetected: FormControl<boolean>;
  /** 明暗検知 */
  lightExposureDetected: FormControl<boolean>;
  /** 衝撃検知 */
  shockDetected: FormControl<boolean>;
  /** フライトモード */
  fmd: FormControl<boolean>;
}>;

@Component({
  selector: 'app-device-type-edit-dialog',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatSlideToggleModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatIconModule,
    MatRadioModule,
    MatGridListModule,
    CommonModule,
  ],
  templateUrl: './device-type-edit-dialog.component.html',
  styleUrl: './device-type-edit-dialog.component.scss',
})
export class DeviceTypeEditDialogComponent implements OnInit {
  /** 更新中 */
  public updating: boolean;

  /** フォームグループ */
  public formGroup: DeviceTypeEditorDialogFormGroup;

  public isDisabled: boolean = false;

  /** 更新対象のデバイス種類内部キー */
  private targetDeviceTypeKey?: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) options: DeviceTypeEditorDialogViewModel,
    private dialogRef: MatDialogRef<DeviceTypeEditorDialogFormGroup>,
    private dialog: MatDialog,
  ) {
    this.updating = options.updating;
    this.targetDeviceTypeKey = options.target?.deviceTypeKey;

    this.formGroup = new FormGroup({
      deviceTypeId: new FormControl<string>(
        options.target?.deviceTypeId || '',
        {
          nonNullable: true,
          validators: [Validators.required, Validators.maxLength(128)],
        },
      ),
      displayName: new FormControl<string>(options.target?.displayName || '', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(256)],
      }),
      description: new FormControl<string>(options.target?.description || '', {
        nonNullable: true,
        validators: [Validators.maxLength(1024)],
      }),
      samplingFrequency: new FormControl<string>(
        options.target?.samplingFrequency?.join(', ') || '',
        {
          nonNullable: true,
          validators: [
            Validators.required,
            Validators.pattern(/^[0-9]+(,\s*[0-9]+)*$/),
            Validators.maxLength(128),
          ],
        },
      ),
      reportingFrequency: new FormControl<string>(
        options.target?.reportingFrequency?.join(', ') || '',
        {
          nonNullable: true,
          validators: [
            Validators.required,
            Validators.pattern(/^[0-9]+(,\s*[0-9]+)*$/),
            Validators.maxLength(128),
          ],
        },
      ),
      temperatureSensor: new FormControl<boolean>(
        options.target?.temperatureSensor || false,
        {
          nonNullable: true,
          validators: [Validators.required],
        },
      ),
      pressureSensor: new FormControl<boolean>(
        options.target?.pressureSensor || false,
        {
          nonNullable: true,
          validators: [Validators.required],
        },
      ),
      humiditySensor: new FormControl<boolean>(
        options.target?.humiditySensor || false,
        {
          nonNullable: true,
          validators: [Validators.required],
        },
      ),
      tiltSensor: new FormControl<boolean>(
        options.target?.tiltSensor || false,
        {
          nonNullable: true,
          validators: [Validators.required],
        },
      ),
      lightSensor: new FormControl<boolean>(
        options.target?.lightSensor || false,
        {
          nonNullable: true,
          validators: [Validators.required],
        },
      ),
      locationSensor: new FormControl<boolean>(
        options.target?.locationSensor || false,
        {
          nonNullable: true,
          validators: [Validators.required],
        },
      ),
      locationTechGnss: new FormControl<boolean>(
        options.target?.locationTechGnss || false,
        {
          nonNullable: true,
          validators: [Validators.required],
        },
      ),
      locationTechWifi: new FormControl<boolean>(
        options.target?.locationTechWifi || false,
        {
          nonNullable: true,
          validators: [Validators.required],
        },
      ),
      locationTechCellular: new FormControl<boolean>(
        options.target?.locationTechCellular || false,
        {
          nonNullable: true,
          validators: [Validators.required],
        },
      ),
      fallDetected: new FormControl<boolean>(
        options.target?.fallDetected || false,
        {
          nonNullable: true,
          validators: [Validators.required],
        },
      ),
      mcdDetected: new FormControl<boolean>(
        options.target?.mcdDetected || false,
        {
          nonNullable: true,
          validators: [Validators.required],
        },
      ),
      lightExposureDetected: new FormControl<boolean>(
        options.target?.lightExposureDetected || false,
        {
          nonNullable: true,
          validators: [Validators.required],
        },
      ),
      shockDetected: new FormControl<boolean>(
        options.target?.shockDetected || false,
        {
          nonNullable: true,
          validators: [Validators.required],
        },
      ),
      fmd: new FormControl<boolean>(options.target?.fmd || false, {
        nonNullable: true,
        validators: [Validators.required],
      }),
    });
  }

  public ngOnInit(): void {
    // 位置即位の初期設定
    if (!this.formGroup.get('locationSensor')?.value) {
      this.formGroup.get('locationTechGnss')?.disable();
      this.formGroup.get('locationTechWifi')?.disable();
      this.formGroup.get('locationTechCellular')?.disable();
    }
    // 位置即位の値が変化した時
    this.formControlValueChanged();

    // デバイス種類の値が "QTS112" の時
    this.samplingFrequencyFieldChange();

    // 位置測位技術が全て空である場合
    this.isLocationPrioritiesEmpty();
  }

  /**
   * 送信
   */
  public async submit() {
    const displayName = this.formGroup.get('displayName')?.value || '';
    const operation = this.updating ? '変更' : '新規登録';
    const options: AlertDialogOption = {
      type: 'warn',
      title: 'Confirm',
      message: `デバイス種類「${displayName}」を${operation}してよろしいですか？`,
      operation: operation,
    };

    const result: boolean = await firstValueFrom(
      this.dialog
        .open(AlertDialogComponent, {
          width: '600px',
          data: options,
        })
        .afterClosed(),
    );
    if (!result) {
      return;
    }

    const deviceType: EditedDeviceTypeViewModel = {
      deviceTypeKey: this.targetDeviceTypeKey || '',
      deviceTypeId: this.formGroup.get('deviceTypeId')?.value || '',
      displayName: this.formGroup.get('displayName')?.value || '',
      description: this.formGroup.get('description')?.value || '',
      samplingFrequency: this.formGroup.get('samplingFrequency')?.value
        ? this.formGroup.get('samplingFrequency')?.value.split(',').map(Number)
        : undefined,
      reportingFrequency: this.formGroup
        .get('reportingFrequency')
        ?.value.split(',')
        .map(Number) || [0],
      temperatureSensor:
        this.formGroup.get('temperatureSensor')?.value || false,
      pressureSensor: this.formGroup.get('pressureSensor')?.value || false,
      humiditySensor: this.formGroup.get('humiditySensor')?.value || false,
      tiltSensor: this.formGroup.get('tiltSensor')?.value || false,
      lightSensor: this.formGroup.get('lightSensor')?.value || false,
      locationSensor: this.formGroup.get('locationSensor')?.value || false,
      locationTechGnss: this.formGroup.get('locationTechGnss')?.value || false,
      locationTechWifi: this.formGroup.get('locationTechWifi')?.value || false,
      locationTechCellular:
        this.formGroup.get('locationTechCellular')?.value || false,
      fallDetected: this.formGroup.get('fallDetected')?.value || false,
      mcdDetected: this.formGroup.get('mcdDetected')?.value || false,
      lightExposureDetected:
        this.formGroup.get('lightExposureDetected')?.value || false,
      shockDetected: this.formGroup.get('shockDetected')?.value || false,
      fmd: this.formGroup.get('fmd')?.value || false,
    };
    this.dialogRef.close(deviceType);
  }

  /**
   * 測定間隔の状態を変更する
   * @returns 処理終了
   */
  public samplingFrequencyFieldChange(): void {
    if (this.formGroup.get('deviceTypeId')?.value === 'QTS112') {
      this.formGroup.get('samplingFrequency')?.disable();
      return;
    }

    this.formGroup.get('samplingFrequency')?.enable();
  }

  // 位置測位をオフにしたら位置測位技術のラジオボタンをdisableにする
  private formControlValueChanged(): void {
    this.formGroup
      .get('locationSensor')
      ?.valueChanges.subscribe((isEnabled: boolean) => {
        // 位置測位をオフにしたとき
        if (!isEnabled) {
          this.formGroup.get('locationTechGnss')?.disable();
          this.formGroup.get('locationTechWifi')?.disable();
          this.formGroup.get('locationTechCellular')?.disable();
          return;
        }

        // 位置測位をオンにした時
        this.formGroup.get('locationTechGnss')?.enable();
        this.formGroup.get('locationTechWifi')?.enable();
        this.formGroup.get('locationTechCellular')?.enable();
      });
  }

  /**
   * 位置測位技術優先度が全て空である場合に必須入力エラーを付与する。
   */
  public isLocationPrioritiesEmpty() {
    const locationTechGnss = this.formGroup.get('locationTechGnss')?.value;
    const locationTechWifi = this.formGroup.get('locationTechWifi')?.value;
    const locationTechCellular = this.formGroup.get(
      'locationTechCellular',
    )?.value;

    if (!locationTechGnss && !locationTechWifi && !locationTechCellular) {
      this.formGroup.get('locationTechGnss')?.setErrors({ required: true });
      this.formGroup.get('locationTechWifi')?.setErrors({ required: true });
      this.formGroup.get('locationTechCellular')?.setErrors({ required: true });
    } else {
      this.formGroup.get('locationTechGnss')?.setErrors(null);
      this.formGroup.get('locationTechWifi')?.setErrors(null);
      this.formGroup.get('locationTechCellular')?.setErrors(null);
    }
  }
}
